import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { deadlineLabel, DeadlineWarning } from "@/components/shared";
import { Inbox, FileCheck2, Trophy, Banknote, ArrowRight } from "lucide-react";

const fmt = (v) => v.toLocaleString("tr-TR", { maximumFractionDigits: 0 });

const Kpi = ({ icon: Icon, label, value, sub, tid }) => (
  <div className="border border-slate-200 bg-white p-5" data-testid={tid}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400">
      <Icon size={13} /> {label}
    </div>
    <div className="font-heading text-3xl font-extrabold tracking-tight mt-2">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function SupplierDashboardPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/supplier/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const acceptedTotal = data.accepted_totals.length === 0
    ? "—"
    : data.accepted_totals.map((t) => `${fmt(t.total)} ${t.currency}`).join(" · ");
  const offeredTotal = data.offered_totals.length === 0
    ? null
    : data.offered_totals.map((t) => `${fmt(t.total)} ${t.currency}`).join(" · ");

  return (
    <div className="space-y-8 fade-in">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Tedarikçi Paneli</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="supplier-kpis">
        <Kpi icon={Inbox} label="Gelen Talep" value={data.request_count} sub="size gönderilen teklif talebi" tid="kpi-requests" />
        <Kpi icon={FileCheck2} label="Verilen Teklif" value={data.offer_count} sub={`yanıt oranı %${data.response_rate}`} tid="kpi-offers" />
        <Kpi icon={Trophy} label="Onaylanan" value={data.accepted_count} sub={`kazanma oranı %${data.win_rate} · ${data.pending_count} değerlendirmede`} tid="kpi-accepted" />
        <Kpi icon={Banknote} label="Onaylanan Tutar" value={acceptedTotal} sub={offeredTotal ? `toplam teklif: ${offeredTotal}` : "henüz onaylanan teklif yok"} tid="kpi-total" />
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-500">Teklif Bekleyen Açık Talepler</div>
          <Link to="/tedarikci/talepler" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 transition-colors duration-200" data-testid="go-all-requests">
            Tümünü Gör <ArrowRight size={12} />
          </Link>
        </div>
        {data.open_requests.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500 text-center" data-testid="open-requests-empty">
            Teklif bekleyen açık talebiniz yok. Yeni talepler geldiğinde burada görünecek.
          </p>
        ) : (
          data.open_requests.map((r) => (
            <Link
              key={r.id}
              to={`/tedarikci/talepler/${r.id}`}
              data-testid={`open-request-${r.id}`}
              className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors duration-200"
            >
              <span className="font-medium text-sm">{r.title}</span>
              <span className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                Son: {deadlineLabel(r)}
                <DeadlineWarning deadline={r.deadline} time={r.deadline_time} />
              </span>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
